import {collection, getDocs, query, QueryConstraint, where} from "firebase/firestore";
import {appFirestore} from "../../../../../index";
import {
    RREmployeeReportItemType,
    RREventReportReportItemType,
    RRReportType
} from "../../../reports/ReportComputing";
import {RREmployeesType, RREmployeeType} from "../../../../types/RREmployeeType";
import {RREventsType, RREventType} from "../../../../types/RREventType";
import {dateToString, dateWithZeroTime} from "../../../../tools";
import {RRLogisticType} from "../../RRLogisticType";
import {logisticsFBConverter} from "../../LogisticsFBConverter";
import {RRDEPARTMENT_LOGISTICS} from "../../../../types/RRDepartmentType";
import {calculateLogisticsReportHours} from "../time-report/LogisticsTimeReportComputing";

type LogisticsTimeBreakdown = {
    regularMinutes: number;
    overtimeMinutes: number;
    price: number;
}

type DescriptionItem = {
    name: string;
    minutes: number;
    price: number;
}

export type ResolvedLogisticPeriod = {
    reportStartDate: Date;
    reportEndDate: Date;
}

const WAREHOUSE_EVENT_KEY = "WAREHOUSE";
export const LOGISTICS_NO_EVENT_EVENT_KEY = "NO_EVENT";

export function resolveLogisticPeriod(logistic: RRLogisticType): ResolvedLogisticPeriod | null {
    if (logistic.dayType === "DAYOFF") {
        return null;
    }

    if (logistic.dayType !== "WORKDAY") {
        const reportStartDate = new Date(logistic.startDate);
        reportStartDate.setHours(8, 0, 0, 0);

        const reportEndDate = new Date(logistic.endDate);
        reportEndDate.setHours(16, 0, 0, 0);

        return {reportStartDate, reportEndDate};
    }

    // Складовите записи се приемат за изработени по планирания им интервал.
    if (logistic.workInStore) {
        return {
            reportStartDate: new Date(logistic.startDate),
            reportEndDate: new Date(logistic.endDate),
        };
    }

    if (!logistic.reportStartDate || !logistic.reportEndDate) {
        return null;
    }

    return {
        reportStartDate: new Date(logistic.reportStartDate),
        reportEndDate: new Date(logistic.reportEndDate),
    };
}

export function isResolvedPeriodStartInRange(
    resolvedPeriod: ResolvedLogisticPeriod,
    periodFrom: Date,
    periodTo: Date
): boolean {
    return resolvedPeriod.reportStartDate >= periodFrom && resolvedPeriod.reportStartDate <= periodTo;
}

export function mergeLogisticsById(...logisticSets: RRLogisticType[][]): RRLogisticType[] {
    const logisticsById = new Map<string, RRLogisticType>();

    logisticSets.flat().forEach(logistic => {
        if (!logistic.id) {
            return;
        }

        logisticsById.set(logistic.id, logistic);
    });

    return Array.from(logisticsById.values());
}

// Изчислението следва досегашната транспортна справка, за да съвпадат часовете и сумите.
export function calculateLogisticBreakdown(
    logistic: RRLogisticType,
    employee: RREmployeeType,
    holidayDates: string[],
    resolvedPeriod: ResolvedLogisticPeriod
): LogisticsTimeBreakdown {
    const reportDate = dateToString(resolvedPeriod.reportStartDate);
    const isBankHoliday = !!reportDate && holidayDates.includes(reportDate);
    const isEmployeeHoliday = logistic.dayType === "HOLIDAY";
    const {regularHours, overtimeHours} = calculateLogisticsReportHours({
        reportStartDate: resolvedPeriod.reportStartDate,
        reportEndDate: resolvedPeriod.reportEndDate,
        isBankHoliday,
        isEmployeeHoliday,
    });

    const regularMinutes = Math.round(regularHours * 60);
    const overtimeMinutes = Math.round(overtimeHours * 60);
    const price = regularHours * (employee.pph || 0) + overtimeHours * (employee.supervisor_pph || 0);

    return {
        regularMinutes,
        overtimeMinutes,
        price,
    };
}

function getLogisticEventKey(logistic: RRLogisticType): string {
    if (logistic.workInStore) {
        return WAREHOUSE_EVENT_KEY;
    }

    const eventNumber = logistic.eventNumber.trim();
    return eventNumber.length > 0 ? eventNumber : LOGISTICS_NO_EVENT_EVENT_KEY;
}

function createSyntheticEvent(eventNumber: string, eventName: string): RREventType {
    return {
        id: `LOGISTICS_${eventNumber}`,
        eventNumber,
        eventName,
        eventShortName: eventName,
        eventStatus: "ЗАВЪРШЕНО",
        eventCategory: RRDEPARTMENT_LOGISTICS,
        clientEmail: "",
        clientName: "",
        isCatering: false,
        isStore: false,
        isMontage: false,
        isDeleted: false,
        face: "",
        notes: "",
        place: "",
        firstDate: new Date(),
        lastDate: null,
        clientPhone: "",
        clientSpoc: "",
    };
}

function getLogisticEvent(eventKey: string, allEvents: RREventsType): RREventType {
    if (eventKey === WAREHOUSE_EVENT_KEY) {
        return createSyntheticEvent(WAREHOUSE_EVENT_KEY, "СКЛАД");
    }
    if (eventKey === LOGISTICS_NO_EVENT_EVENT_KEY) {
        return createSyntheticEvent(LOGISTICS_NO_EVENT_EVENT_KEY, "БЕЗ СЪБИТИЕ");
    }

    const existingEvent = allEvents.find(event => event.eventNumber === eventKey);
    return existingEvent ? existingEvent : createSyntheticEvent(eventKey, eventKey);
}

function getDayTypeLabel(dayType: RRLogisticType["dayType"]): string {
    if (dayType === "HOLIDAY") {
        return "ОТПУСК";
    }
    if (dayType === "SICK") {
        return "БОЛНИЧЕН";
    }
    if (dayType === "DAYOFF") {
        return "ОТСЪСТВИЕ";
    }
    return "РАБОТА";
}

function buildMissingEventDocumentLabel(
    logistic: RRLogisticType,
    employee: RREmployeeType,
    resolvedPeriod: ResolvedLogisticPeriod
): string {
    const truckPlate = logistic.truck.plate.trim();

    return [
        employee.name.trim() || "-",
        dateToString(resolvedPeriod.reportStartDate),
        truckPlate || "-",
    ].join(" | ");
}

function buildDescriptionLabel(
    logistic: RRLogisticType,
    employee: RREmployeeType,
    resolvedPeriod: ResolvedLogisticPeriod,
    eventKey: string
): string {
    if (eventKey === LOGISTICS_NO_EVENT_EVENT_KEY) {
        return buildMissingEventDocumentLabel(logistic, employee, resolvedPeriod);
    }

    const parts: string[] = [];

    if (logistic.dayType !== "WORKDAY") {
        parts.push(getDayTypeLabel(logistic.dayType));
    } else if (logistic.workInStore) {
        parts.push("СКЛАД");
    } else if (logistic.eventNumber.trim().length > 0) {
        parts.push(`Събитие ${logistic.eventNumber.trim()}`);
    }

    if (logistic.startPoint || logistic.endPoint) {
        parts.push(`${logistic.startPoint || "?"} -> ${logistic.endPoint || "?"}`);
    }

    if (logistic.reportNotes.trim().length > 0) {
        parts.push(logistic.reportNotes.trim());
    } else if (logistic.planNotes.trim().length > 0) {
        parts.push(logistic.planNotes.trim());
    }

    return parts.length > 0 ? parts.join(" | ") : "Отчетен запис";
}

function appendDescriptionItem(
    description: string | undefined,
    item: DescriptionItem
): string {
    const items: DescriptionItem[] = description ? parseDescriptionItems(description) : [];
    items.push(item);
    return JSON.stringify(items);
}

function parseDescriptionItems(description: string): DescriptionItem[] {
    try {
        const parsed = JSON.parse(description) as DescriptionItem[];
        return Array.isArray(parsed) ? parsed : [];
    } catch (_error) {
        return description ? [{name: description, minutes: 0, price: 0}] : [];
    }
}

function getDescriptionMinutes(eventKey: string, totalMinutes: number): number {
    return eventKey === LOGISTICS_NO_EVENT_EVENT_KEY ? 0 : totalMinutes;
}

function createEmployeeReportItem(
    logistic: RRLogisticType,
    employee: RREmployeeType,
    reportDate: string,
    breakdown: LogisticsTimeBreakdown,
    resolvedPeriod: ResolvedLogisticPeriod,
    eventKey: string
): RREmployeeReportItemType {
    const totalMinutes = breakdown.regularMinutes + breakdown.overtimeMinutes;
    const descriptionLabel = buildDescriptionLabel(logistic, employee, resolvedPeriod, eventKey);

    return {
        date: reportDate,
        employee,
        price: breakdown.price,
        minutes: breakdown.regularMinutes,
        minutesAsHoliday: 0,
        minutesAsSupervisor: breakdown.overtimeMinutes,
        description: appendDescriptionItem(undefined, {
            name: descriptionLabel,
            minutes: getDescriptionMinutes(eventKey, totalMinutes),
            price: breakdown.price,
        }),
    };
}

function mergeEmployeeReportItem(
    items: RREmployeeReportItemType[],
    employeeItem: RREmployeeReportItemType
): void {
    const existingEmployeeItem = items.find(
        item => item.employee.id === employeeItem.employee.id && item.date === employeeItem.date
    );

    if (existingEmployeeItem) {
        existingEmployeeItem.minutes += employeeItem.minutes;
        existingEmployeeItem.minutesAsSupervisor += employeeItem.minutesAsSupervisor;
        existingEmployeeItem.minutesAsHoliday += employeeItem.minutesAsHoliday;
        existingEmployeeItem.price += employeeItem.price;
        parseDescriptionItems(employeeItem.description || "").forEach(descriptionItem => {
            existingEmployeeItem.description = appendDescriptionItem(
                existingEmployeeItem.description,
                descriptionItem
            );
        });
        return;
    }

    items.push({...employeeItem});
}

export default async function calculateLogisticsWorkReport(
    periodFrom: Date,
    periodTo: Date,
    employees: RREmployeesType,
    holidayDates: string[],
    allEvents: RREventsType,
    onlyForEmployeeId?: string
): Promise<RRReportType> {
    const report: RRReportType = {
        periodFrom: dateWithZeroTime(periodFrom)!,
        periodTo: dateWithZeroTime(periodTo)!,
        report: {
            events: [],
            employees: [],
        }
    };

    const logisticsCollection = collection(appFirestore, "logistics").withConverter(logisticsFBConverter);
    const getLogisticsConstraints = (dateField: "startDate" | "reportStartDate"): QueryConstraint[] => {
        const constraints: QueryConstraint[] = [
            where(dateField, ">=", periodFrom),
            where(dateField, "<=", periodTo),
        ];
        if (onlyForEmployeeId) {
            constraints.push(where("driverId", "==", onlyForEmployeeId));
        }

        return constraints;
    };

    const [plannedStartSnapshot, reportedStartSnapshot] = await Promise.all([
        getDocs(query(logisticsCollection, ...getLogisticsConstraints("startDate"))),
        getDocs(query(logisticsCollection, ...getLogisticsConstraints("reportStartDate"))),
    ]);
    const logistics = mergeLogisticsById(
        plannedStartSnapshot.docs.map(doc => doc.data()),
        reportedStartSnapshot.docs.map(doc => doc.data())
    );

    for (const logistic of logistics) {
        const employee = employees.find(item => item.id === logistic.driverId);
        if (!employee) {
            continue;
        }

        const resolvedPeriod = resolveLogisticPeriod(logistic);
        if (!resolvedPeriod) {
            continue;
        }

        if (!isResolvedPeriodStartInRange(resolvedPeriod, periodFrom, periodTo)) {
            continue;
        }

        const reportDate = dateToString(resolvedPeriod.reportStartDate);
        if (!reportDate) {
            continue;
        }

        const breakdown = calculateLogisticBreakdown(logistic, employee, holidayDates, resolvedPeriod);
        const totalMinutes = breakdown.regularMinutes + breakdown.overtimeMinutes;
        const eventKey = getLogisticEventKey(logistic);
        const descriptionLabel = buildDescriptionLabel(logistic, employee, resolvedPeriod, eventKey);
        const descriptionMinutes = getDescriptionMinutes(eventKey, totalMinutes);

        const employeeItem = createEmployeeReportItem(logistic, employee, reportDate, breakdown, resolvedPeriod, eventKey);
        const existingEmployeeItem = report.report.employees.find(
            item => item.employee.id === employee.id && item.date === reportDate
        );
        if (existingEmployeeItem) {
            existingEmployeeItem.minutes += employeeItem.minutes;
            existingEmployeeItem.minutesAsSupervisor += employeeItem.minutesAsSupervisor;
            existingEmployeeItem.minutesAsHoliday += employeeItem.minutesAsHoliday;
            existingEmployeeItem.price += employeeItem.price;
            existingEmployeeItem.description = appendDescriptionItem(existingEmployeeItem.description, {
                name: descriptionLabel,
                minutes: descriptionMinutes,
                price: breakdown.price,
            });
        } else {
            report.report.employees.push({...employeeItem});
        }

        const event = getLogisticEvent(eventKey, allEvents);
        const existingEventItem = report.report.events.find(
            item => item.event.eventNumber === event.eventNumber && item.date === reportDate
        );

        if (existingEventItem) {
            existingEventItem.minutes += breakdown.regularMinutes;
            existingEventItem.minutesAsSupervisor += breakdown.overtimeMinutes;
            existingEventItem.minutesAsHoliday += 0;
            existingEventItem.price += breakdown.price;
            existingEventItem.description = appendDescriptionItem(existingEventItem.description, {
                name: descriptionLabel,
                minutes: descriptionMinutes,
                price: breakdown.price,
            });
            mergeEmployeeReportItem(existingEventItem.employees, employeeItem);
        } else {
            const eventItem: RREventReportReportItemType = {
                date: reportDate,
                event,
                employees: [{...employeeItem}],
                minutes: breakdown.regularMinutes,
                minutesAsSupervisor: breakdown.overtimeMinutes,
                minutesAsHoliday: 0,
                price: breakdown.price,
                description: appendDescriptionItem(undefined, {
                    name: descriptionLabel,
                    minutes: descriptionMinutes,
                    price: breakdown.price,
                }),
            };
            report.report.events.push(eventItem);
        }
    }

    return report;
}
